import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Translate } from 'react-localize-redux';
import ReviewGallery from './ReviewGallery';
import { ImageAssessmentValue } from './ImageAssessment';
import AssessmentStatus from './AssessmentStatus';
import LesionReviewDialog from './LesionReviewDialog';
import LesionPanel, { LesionFeedbackValue } from './LesionPanel';
import { IdentifiableCorrection } from './ImageReviewCard';
import { ReviewImage, dedupeImagesByFile } from './imageMeta';
import { QcCategoriesState } from './qcCategories';
import DiagnosticWorkup from './DiagnosticWorkup';
import { QcDiagnosticWorkupDto } from '../../../services/qualityCheck';

interface LesionReviewSectionProps {
    /** The eCRF repeat-group record this lesion's shots hang off. */
    groupRecordUuid: string;
    /** 1-based position of this lesion within the visit. */
    lesionNumber: number;
    /**
     * SKIN-441 — this lesion instance carries no `lesion_diagnoses` answer.
     *
     * Joined by `groupRecordUuid` from the visit payload rather than read off the
     * images, because the answer belongs to the lesion and not to any photograph of it.
     *
     * This chip is a **locator, not the count**. Sections are built from images
     * (`groupImagesByLesion`), so a lesion answered without any photograph renders no
     * section and therefore no chip — it is counted in the visit header's
     * `undiagnosedLesionCount` and cannot be marked here. The header is what states the
     * total; this only points at the ones a reviewer can click.
     *
     * Defaults to false, so a lesion whose diagnosis the payload says nothing about
     * renders unmarked rather than accused.
     */
    undiagnosed?: boolean;
    /**
     * SKIN-611 — the diagnostic workup recorded inside this lesion instance (final
     * diagnosis, lab answers…), read-only. Null or absent: the lesion answered none of it,
     * and nothing renders. Never the visit-level D1, which has its own block.
     */
    diagnosticWorkup?: QcDiagnosticWorkupDto | null;
    images: ReviewImage[];
    canViewIdentifiable: boolean;
    readOnly?: boolean;
    corrections?: Record<string, IdentifiableCorrection>;
    onCorrectIdentifiable?: (encryptedFileUuid: string) => void;
    onRetry?: (encryptedFileUuid: string) => void;

    /** The lesion-level categories for this study, and where that fetch has got to. */
    lesionCategories: QcCategoriesState;
    lesionValue: LesionFeedbackValue;
    onLesionChange: (next: LesionFeedbackValue) => void;

    /**
     * The categories for one image, resolved by its eCRF field. A function rather than a
     * map because the API serves them per field name, and several images on a lesion share
     * a field — so the caller caches by field, not by image.
     *
     * It returns the fetch state, not just the sections, so one field's failure shows on
     * that field's cards alone. Two visit-wide flags painted the warning on every image in
     * the lesion, including the ones whose categories had already arrived.
     */
    categoriesFor: (image: ReviewImage) => QcCategoriesState;
    assessmentFor: (encryptedFileUuid: string) => ImageAssessmentValue;
    onAssessmentChange: (encryptedFileUuid: string, next: ImageAssessmentValue) => void;
}

/**
 * SKIN-241 — one lesion: its shot set, and the panel that governs it.
 *
 * This is the unit the visit screen repeats, and it exists because **a visit is not a
 * lesion**. The design handoff renders lesion feedback once at the bottom of the visit;
 * measured against production that is wrong for roughly a fifth of visits — 306 carry one
 * lesion, 78 carry two or more, and one carries 16 — and a shared panel would attribute
 * one lesion's missing shot to every other lesion on the visit.
 *
 * The panel sits with the shots it describes rather than at the end of the page, so the
 * reviewer never has to work out which set a complaint belongs to.
 *
 * It holds no review state. Every value comes from the caller keyed on the anchor the API
 * uses — `encryptedFileUuid` for an image, `groupRecordUuid` for the lesion — because those
 * are the same anchors the upserts are addressed to, and a component that kept its own copy
 * would let the screen and the server disagree. The one thing it does hold is where its
 * review dialog is open (SKIN-784), which is UI state the server has no opinion on.
 *
 * SKIN-784 — the photographs are assessed in that dialog, one at a time and large; the
 * cards say where each one stands in a single line.
 */
export default function LesionReviewSection({
    groupRecordUuid,
    lesionNumber,
    undiagnosed = false,
    diagnosticWorkup = null,
    images,
    canViewIdentifiable,
    readOnly = false,
    corrections,
    onCorrectIdentifiable,
    onRetry,
    lesionCategories,
    lesionValue,
    onLesionChange,
    categoriesFor,
    assessmentFor,
    onAssessmentChange,
}: LesionReviewSectionProps) {
    // Every image on a lesion carries the same body region — it is a lesion-level eCRF
    // answer (`body_map_code`), not a per-image one — so the first that has it names the
    // group.
    const bodyRegion = images.find((image) => image.bodyRegion)?.bodyRegion ?? null;

    // Counted after duplicates collapse, as the rail counts — or "X of Y" would never reach Y.
    const unique = dedupeImagesByFile(images);
    const assessed = unique.filter((image) => assessmentFor(image.encryptedFileUuid).outcome !== null).length;
    const firstUnassessed = unique.find((image) => assessmentFor(image.encryptedFileUuid).outcome === null);
    const reviewLabel = readOnly
        ? 'view'
        : assessed === 0 ? 'review' : firstUnassessed ? 'continue' : 'again';

    const [openAt, setOpenAt] = React.useState<string | null>(null);

    return (
        <Box
            sx={{ mb: 3 }}
            data-testid="lesion-review-section"
            data-group-record-uuid={groupRecordUuid}
        >
            {/*
              * `component`, not just `variant`: MUI maps both subtitle variants to `h6`, so
              * the page ran the dashboard's `h3` section title straight into `h6` headings
              * with nothing between. The visit title above is `h4`; this is its child.
              */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle1" component="h5" data-testid="lesion-heading">
                    <Translate id="hospital.quality-check.review.lesion" />
                    {` ${lesionNumber}`}
                    {bodyRegion ? ` · ${bodyRegion}` : ''}
                    {undiagnosed && (
                        <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 1 }}
                            data-testid="lesion-no-diagnosis"
                            label={<Translate id="hospital.quality-check.review.no-diagnosis" />}
                        />
                    )}
                </Typography>
                {unique.length > 0 && (
                    <React.Fragment>
                        <Chip
                            size="small"
                            data-testid="lesion-progress"
                            color={firstUnassessed ? 'default' : 'success'}
                            variant={firstUnassessed ? 'outlined' : 'filled'}
                            label={(
                                <React.Fragment>
                                    {`${assessed} / ${unique.length} `}
                                    <Translate id="hospital.quality-check.review.reviewed-count" />
                                </React.Fragment>
                            )}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Button
                            variant={reviewLabel === 'review' || reviewLabel === 'continue' ? 'contained' : 'outlined'}
                            startIcon={<VisibilityOutlinedIcon />}
                            data-testid="review-lesion"
                            data-label={reviewLabel}
                            // "Review again" and "View" start at the first photograph: there is
                            // nothing unassessed to jump to.
                            onClick={() => setOpenAt((readOnly ? unique[0] : firstUnassessed ?? unique[0]).encryptedFileUuid)}
                        >
                            <Translate
                                id={reviewLabel === 'view'
                                    ? 'general.view'
                                    : reviewLabel === 'review'
                                        ? 'hospital.quality-check.review.review-lesion'
                                        : reviewLabel === 'continue'
                                            ? 'hospital.quality-check.review.continue-review'
                                            : 'hospital.quality-check.review.review-again'}
                            />
                        </Button>
                    </React.Fragment>
                )}
            </Box>

            {/*
              * Above the shots, so the reviewer reads what the lesion was diagnosed with
              * before checking the photographs against it.
              */}
            {diagnosticWorkup && (
                <Box sx={{ mb: 1.5 }}>
                    <DiagnosticWorkup workup={diagnosticWorkup} />
                </Box>
            )}

            <ReviewGallery
                images={images}
                // One channel for the verdict. The card's frame and the status line inside it
                // state the same fact, so resolving it here — rather than cloning every
                // image to overwrite a field documented as the server's — keeps them from
                // ever disagreeing.
                outcomeFor={(image) => assessmentFor(image.encryptedFileUuid).outcome}
                canViewIdentifiable={canViewIdentifiable}
                readOnly={readOnly}
                corrections={corrections}
                onCorrectIdentifiable={onCorrectIdentifiable}
                onRetry={onRetry}
                renderAssessment={(image) => (
                    <AssessmentStatus categories={categoriesFor(image)} value={assessmentFor(image.encryptedFileUuid)} />
                )}
                onOpen={setOpenAt}
            />

            <LesionReviewDialog
                lesionNumber={lesionNumber}
                images={images}
                openAt={openAt}
                canViewIdentifiable={canViewIdentifiable}
                readOnly={readOnly}
                categoriesFor={categoriesFor}
                assessmentFor={assessmentFor}
                onAssessmentChange={onAssessmentChange}
                onRetry={onRetry}
                corrections={corrections}
                onCorrectIdentifiable={onCorrectIdentifiable}
                onClose={() => setOpenAt(null)}
            />

            <LesionPanel
                categories={lesionCategories}
                bodyRegion={bodyRegion}
                // Deduplicated, because the gallery above renders the collapsed list: the
                // eCRF records the same photograph once per submission, so counting the raw
                // input would claim more shots than there are cards.
                imageCount={unique.length}
                value={lesionValue}
                onChange={onLesionChange}
                readOnly={readOnly}
            />
        </Box>
    );
}
